import { ApolloError } from '@apollo/client'
import { navigate, Location } from '@reach/router'
import * as React from 'react'
import styled, { css } from 'styled-components'
import link, { Link } from '../../../../common/src/components/Link'
import { BodyTextSpan, Spacer, DownArrow, IntroTextSpan } from '../../../../common/src/components/core'
import { Colors, Fonts } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import xhr from '../../../../common/src/data/xhr'
import { addListener, Feedback, FeedbackType, removeListener } from '../../../../common/src/feedback'
import keyboard, { KeyAction } from '../../../../common/src/keyboard'
import { FetchUserContextQuery, UserType } from '../../../../common/src/types.gen'
import { lookAtUser } from '../../graphql/mutations'
import GoogleLogout from '../GoogleLogout'
import { UserStatus } from '../components/UserStatus'
import LogoAnimated from './LogoAnimated'
import OmniSearch from './OmniSearch'
import ShortcutsModal from './ShortcutsModal'

/**
 * Height of the header/navbar, in pixels.
 */
export const HEADER_HEIGHT = 66

interface HeaderDropdownProps {
  user: FetchUserContextQuery['self']
  isDown: boolean
  onToggle: () => void
}

export interface HeaderProps {
  user: FetchUserContextQuery['self']
  commitHash?: string
}

export interface HeaderState {
  view: 'NORMAL' | 'SEARCH' | 'DROPDOWN' | 'SHORTCUTS'
  feedback?: Feedback
}

export default class Header extends React.Component<HeaderProps, HeaderState> {
  private feedbackTimeout = undefined as number | undefined

  constructor(props: Header['props']) {
    super(props)
    this.state = { view: 'NORMAL' }
  }

  public componentDidMount() {
    keyboard.addListener(this.onKeyboardBound)
    addListener(this.onFeedbackBound)
  }

  public componentWillUnmount() {
    keyboard.removeListener(this.onKeyboardBound)
    removeListener(this.onFeedbackBound)
  }

  public render() {
    const { user, commitHash } = this.props
    const { view, feedback } = this.state
    const isDev = commitHash != null
    const isSenior = user.userType !== UserType.OPERATOR && user.userType !== UserType.TRAINING

    return (
      <DivHeader isDev={isDev}>
        <LogoAndShortcutsWrapper>
          <LogoLink>
            <Link to="/leasing" noTab title="Lisa">
              <LogoAnimated />
            </Link>
          </LogoLink>
          <HeaderSpan>
            <Link onClick={this.showShortcutsBound} noTab>
              Shortcuts
            </Link>
          </HeaderSpan>
        </LogoAndShortcutsWrapper>
        <DivSearch isExpanded={view === 'SEARCH'}>
          <OmniSearch onBlur={this.hideSearchBound} onFocus={this.showSearchBound} />
        </DivSearch>
        {view !== 'SEARCH' && (
          <DivMiddle>
            <TopNavLink to="/leasing/lobby" text="Lobby" />
            {isSenior && <TopNavLink to="/leasing/status" text="Status" />}
            <TopNavLink to="/leasing/tasks" text="Tasks" />
            <TopNavLink to="/leasing/inbox" text="Inbox" />
          </DivMiddle>
        )}
        <DivRight>
          {view !== 'SEARCH' && <HeaderFeedback feedback={feedback} />}
          <HeaderDropdown user={user} isDown={view === 'DROPDOWN'} onToggle={this.toggleShowDropdownBound} />
        </DivRight>
        {view === 'SHORTCUTS' && <ShortcutsModal onClose={this.closeShortcutsBound} />}
      </DivHeader>
    )
  }

  private showShortcutsBound = () => this.setState({ view: 'SHORTCUTS' })

  private closeShortcutsBound = () => this.setState({ view: 'NORMAL' })

  private onFeedbackBound = (feedback: Feedback) => {
    this.setState({ feedback })
    if (this.feedbackTimeout) {
      window.clearTimeout(this.feedbackTimeout)
    }
    this.feedbackTimeout = window.setTimeout(() => this.setState({ feedback: undefined }), 3000)
  }

  private onKeyboardBound = (action: KeyAction) => {
    if (action === KeyAction.lobby) {
      navigate('/leasing/lobby').catch(handleError)
    } else if (action === KeyAction.inbox) {
      navigate('/leasing/inbox').catch(handleError)
    } else if (action === KeyAction.tasks) {
      navigate('/leasing/tasks').catch(handleError)
    }
  }

  private toggleShowDropdownBound = () => {
    const hasDropdown = this.state.view === 'DROPDOWN'
    this.setState({ view: hasDropdown ? 'NORMAL' : 'DROPDOWN' })
  }

  private showSearchBound = () => {
    this.setState({ view: 'SEARCH' })
  }

  private hideSearchBound = () => {
    this.setState({ view: 'NORMAL' })
  }
}

const HeaderLabelBodyText = styled(BodyTextSpan)`
  width: 150px;
  display: inline-block;
  text-align: right;
`

export class HeaderDropdown extends React.PureComponent<HeaderDropdownProps> {
  public render() {
    const { isDown, onToggle, user } = this.props

    return (
      <DropdownWrap onClick={onToggle}>
        <DropdownLabel>
          <HeaderLabelBodyText>
            <UserStatus user={user} /> <Spacer width={4} /> <DownArrow isDown={isDown} />
          </HeaderLabelBodyText>
        </DropdownLabel>
        {isDown && this.renderNavDropdown()}
      </DropdownWrap>
    )
  }

  private renderNavDropdown() {
    const { user, onToggle } = this.props

    const showAdminOptions = user.permissions.canViewAdminPage

    // Note the API Explorer link has to be a hard link, not history.push:
    return (
      <DropdownMenu onClick={onToggle}>
        <DropdownNavCol>
          <DropdownMenuLinkWrap>
            <DropdownMenuLink to="/leasing/inbox" onClick={this.handleClickAllThreads}>
              All Threads
            </DropdownMenuLink>
          </DropdownMenuLinkWrap>
          {(showAdminOptions || user.userType === UserType.TEAM_LEAD) && (
            <DropdownMenuLinkWrap>
              <DropdownMenuLink to="/leasing/admin">Admin</DropdownMenuLink>
            </DropdownMenuLinkWrap>
          )}
          {showAdminOptions && (
            <>
              <DropdownMenuLinkWrap>
                <DropdownMenuLink href="/leasing/api" noTab>
                  API Explorer
                </DropdownMenuLink>
              </DropdownMenuLinkWrap>
              <DropdownMenuLinkWrap>
                <DropdownMenuLink href="/leasing/javadoc" noTab>
                  Javadoc
                </DropdownMenuLink>
              </DropdownMenuLinkWrap>
            </>
          )}
          <DropdownMenuLinkWrap>
            <DropdownMenuLink href="/leasing/release-notes" noTab>
              Release Notes
            </DropdownMenuLink>
          </DropdownMenuLinkWrap>
          <DropdownMenuLinkWrap>
            <GoogleLogout email={user.email} render={this.renderGoogleLogout} onGoogleSignOut={this.logOutBound} />
          </DropdownMenuLinkWrap>
        </DropdownNavCol>
      </DropdownMenu>
    )
  }

  private renderGoogleLogout = (props: { onClick?: () => void }) => (
    <DropdownMenuSpan onClick={props.onClick}>Sign out</DropdownMenuSpan>
  )

  private handleClickAllThreads = () => lookAtUser({ observedUserId: 0 })

  private logOutBound = () => {
    xhr
      .post('/auth/logout', {})
      .then(() => {
        window.location.href = '/leasing'
      })
      .catch((err: ApolloError) => handleError(err))
  }
}

const TopNavLink = ({ to, text }: { to: string; text: string }) => (
  <Location>
    {({ location }) => (
      <TopNavLinkWrap to={to} selected={location.pathname.startsWith(to)} noTab>
        {text}
      </TopNavLinkWrap>
    )}
  </Location>
)

const HeaderFeedback = ({ feedback }: { feedback?: Feedback }) => {
  const color = feedback && feedback.type === FeedbackType.ERROR ? 'coral' : 'silver'
  return (
    <HeaderFeedbackWrap className="header-feedback" color={color}>
      {feedback && feedback.message}
    </HeaderFeedbackWrap>
  )
}

const DivHeader = styled.div`
  ${(props: { isDev?: boolean }) => css`
    height: ${HEADER_HEIGHT}px;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${Colors.ink};
    border-bottom: solid 1px rgba(47, 44, 48, 0.75);
    ${props.isDev ? `border-top: solid 8px ${Colors.coral};` : ''};
  `};
`

const DivSearch = styled.div`
  ${(props: { isExpanded?: boolean }) => css`
    ${props.isExpanded ? 'width: 64px' : ''};
    flex-grow: ${props.isExpanded ? '1' : '0'};
    text-align: center;
    align-self: center;
    margin: 0 24px;
  `};
`

const DivMiddle = styled.div`
  flex-grow: 1;
  text-align: center;
  align-self: center;
`

const DivRight = styled.div`
  font-size: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: row;
  padding-right: 16px;
`

const HeaderSpan = styled(IntroTextSpan)`
  position: relative;
  top: -4px;
  margin-left: 16px;
`

const LogoLink = styled.div`
  height: 50px;
`

const LogoAndShortcutsWrapper = styled.div`
  min-width: 79px;
  height: 20px;
  margin-left: 24px;
  position: relative;
  top: 6px;
  object-fit: contain;
  filter: none !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`

const TopNavLinkWrap = link(styled.span`
  ${(props: { selected?: boolean }) => css`
    cursor: pointer;
    padding: 12px 24px 13px 24px;
    font-size: 16px;
    font-family: ${Fonts.sansHeader};
    border: solid 1px #000000;
    margin: 0px -0.5px;
    color: ${props.selected ? 'white' : 'rgba(255, 255, 255, 0.25)'};
    background-color: ${props.selected ? 'black' : 'inherit'};
    transition: 0.2s;
    text-decoration: none;

    &:hover {
      color: ${props.selected ? 'white' : 'rgba(255, 255, 255, 0.4)'};
      background-color: ${props.selected ? 'black' : 'rgba(0, 0, 0, 0.2)'};
    }

    &:first-child {
      border-radius: 2px 0 0 2px;
    }

    &:last-child {
      border-radius: 0 2px 2px 0;
    }
  `};
`)

const DropdownWrap = styled.span`
  border: none;
  padding: 10px 16px 8px 0;
  cursor: pointer;
`

const DropdownLabel = styled.span`
  font-family: ${Fonts.sansHeader};
  font-size: 16px;
  color: ${Colors.white};
  user-select: none;
`

const DropdownMenuSpan = styled.span`
  text-decoration: none;
  color: ${(props: { emphasize?: boolean }) => (props.emphasize ? Colors.sky : Colors.white)};
  background: transparent;
  display: block;
  border-radius: 2px;
  padding: 14px 12px;
`

const DropdownMenuLink = link(DropdownMenuSpan)

const DropdownMenuLinkWrap = styled.div`
  &:hover ${DropdownMenuSpan} {
    opacity: 1 !important;
    background-color: #2f2b30;
  }
`

const StationLabel = styled(BodyTextSpan)`
  display: inline-block;
  width: 96px;
`

const StationLinkWrap = styled.div`
  ${({ emphasize }: { emphasize?: boolean }) => css`
    display: inline-block;
    padding: 12px 8px;
    border-radius: 2px;
    transition: opacity 0.2s;
    color: ${emphasize ? Colors.sky : 'white'};
    opacity: ${emphasize ? '1 !important' : 'unset'};

    &:first-child {
      margin-left: -6px;
    }

    &:hover {
      opacity: 1 !important;
      background-color: #2f2b30;
    }
  `}
`

const DropdownMenu = styled.div`
  position: absolute;
  right: 0px;
  top: ${HEADER_HEIGHT}px;
  margin-top: -1px;
  padding: 4px;

  display: flex;
  flex-direction: row;

  background: #111111;
  z-index: 100;
  font: 14px ${Fonts.sansBody};

  border-bottom-left-radius: 4px;
  border: solid 1px #2f2b30;
  border-top: solid 1px #2f2b30;

  &:hover ${DropdownMenuSpan} {
    opacity: 0.25;
  }

  &:hover ${StationLinkWrap} {
    opacity: 0.25;
  }

  &:hover ${StationLabel} {
    opacity: 0.25;
  }
`

const DropdownNavCol = styled.div`
  width: 128px;
`

const HeaderFeedbackWrap = styled(BodyTextSpan)`
  display: inline-block;
  margin: 0 24px 0 0;
  width: 256px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: end;
  vertical-align: top;
`
